import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Stack, Box, IconButton, Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { PayerDownloadCsv, PayerUploadCsv } from '../commands';
import { AddPayerDialog, PayerUploadHelpDialog } from '../dialogs';
import { PayerGrid } from '../grids';
import { useGetPayersQuery, useUpsertPayerMutation } from '../../../../services/ConfigurationService';
import { UploadProgress } from '../../../../services/signalRService';
import { PayerType } from '../../../../services/ConfigurationService/types/payerType';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const PayersTab: React.FC = () => {
  const { id: centerId } = useParams<{ id: string }>();
  const query = useQuery();
  const company = query.get("company") || "";
  
  // Fetch payers data
  const { refetch: refetchPayers } = useGetPayersQuery({ company, centerId: centerId || '' }, { skip: !company || !centerId });
  const [upsertPayer] = useUpsertPayerMutation();
  
  // Upload progress state
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  
  // Help dialog state
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  // Upload handlers
  const handleUploadStart = () => {
    setProgress({
      operationId: '',
      progressPercentage: 0,
      message: 'Starting upload...',
      processedRecords: 0,
      totalRecords: 0,
      timestamp: new Date().toISOString(),
      status: 'Starting',
      uploadType: 'payers'
    });
  };
  
  const handleProgressUpdate = (progress: UploadProgress) => {
    setProgress(progress);
  };
  
  const handleUploadComplete = (progress: UploadProgress) => {
    setProgress(progress);
  };
  
  const handleUploadError = (error: string) => {
    console.error('Payer upload error:', error);
    setProgress(null);
  };
  
  const handleAddPayer = async (payer: PayerType) => {
    try {
      await upsertPayer(payer).unwrap();
      refetchPayers();
    } catch (error) {
      console.error('Failed to add payer:', error);
      throw error;
    }
  };
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <Tooltip title="View upload file specification">
          <IconButton 
            onClick={() => setHelpDialogOpen(true)}
            size="small"
            color="primary"
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
        
        <PayerDownloadCsv
          company={company}
          centerId={centerId || ''}
          onError={(error) => console.error('Download error:', error)}
        />
        <PayerUploadCsv
          company={company}
          centerId={centerId || ''}
          onUploadStart={handleUploadStart}
          onProgressUpdate={handleProgressUpdate}
          onComplete={handleUploadComplete}
          onSuccess={() => {
            refetchPayers();
            console.log('Payers uploaded successfully');
          }}
          onError={handleUploadError}
        />
        <AddPayerDialog
          defaultCenterId={centerId || ''}
          defaultCompany={company}
          onAdd={handleAddPayer}
        />
      </Box>
      <PayerGrid company={company} centerId={centerId || ''} />
      
      {/* Help Dialog */}
      <PayerUploadHelpDialog 
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
    </Stack>
  );
};